import React, { useState, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import './Checker.css';

const Checker = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, fetchUser } = useContext(AuthContext);

  const [text, setText] = useState(location.state?.text || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [listening, setListening] = useState(false);
const recognitionRef = useRef(null);
const finalTranscriptRef = useRef('');
const lastFinalRef = useRef('');
const listeningRef = useRef(false); // ✅ ADD THIS


useEffect(() => {
  return () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  };
}, []);




// ======================================
// VOICE TO TEXT (Speech Recognition)
// ======================================
const toggleVoiceInput = () => {
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;

  if (!SpeechRecognition) {
    toast.error('Voice input not supported in this browser');
    return;
  }

  // 🛑 Stop if already listening
 if (listening && recognitionRef.current) {
  recognitionRef.current.stop();
  recognitionRef.current = null;
  setListening(false);
  listeningRef.current = false; // ✅ CRITICAL
  finalTranscriptRef.current = '';
  lastFinalRef.current = '';
  toast.info('Voice stopped');
  return;
}


  // ▶️ Start listening
  const recognition = new SpeechRecognition();
  recognition.lang = 'en-IN';
  recognition.continuous = true;
  recognition.interimResults = true;

  recognitionRef.current = recognition;
setListening(true);
listeningRef.current = true; // ✅ CRITICAL
toast.info('Listening... Click again to stop');


  recognition.onresult = (event) => {
  let interim = '';
  let finalText = finalTranscriptRef.current;

  for (let i = event.resultIndex; i < event.results.length; i++) {
    const chunk = event.results[i][0].transcript.trim();

    if (event.results[i].isFinal) {
      if (chunk !== lastFinalRef.current) {
        finalText += chunk + ' ';
        lastFinalRef.current = chunk;
      }
    } else {
      interim += chunk + ' ';
    }
  }

  finalTranscriptRef.current = finalText;
  setText((finalText + interim).trim());
};


  recognition.onerror = () => {
    toast.error('Voice recognition error');
    setListening(false);
    recognitionRef.current = null;
  };

  recognition.onend = () => {
  if (listeningRef.current) {
    setTimeout(() => {
      try {
        recognition.start();
      } catch {}
    }, 300);
  }
};

  recognition.start();
};



  // Restore text after login returning from redirect
  useEffect(() => {
    if (location.state?.text) {
      setText(location.state.text);
    }
  }, [location]);

  // ======================================
  // CHECK GRAMMAR
  // ======================================
  const handleCheck = async () => {
    if (!user) {
      toast.error("Create an account to continue");
      return navigate(`/signup?redirect=/checker&text=${encodeURIComponent(text)}`);
    }

    if (!text.trim()) {
      toast.error('Please enter some text to check');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API}/grammar/check`,
        { text },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setResult(response.data);
      await fetchUser();
      toast.success('Grammar check completed!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Daily limit reached. Upgrade to premium!');
        setTimeout(() => navigate('/pricing'), 1500);
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Login again.');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Grammar check failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const getMistakeColor = (type) => {
    const colors = {
      spelling: 'bg-red-500',
      grammar: 'bg-orange-500',
      punctuation: 'bg-yellow-500',
      clarity: 'bg-blue-500',
      structure: 'bg-purple-500',
      capitalization: 'bg-green-500',
      article: 'bg-pink-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  return (
    <div className="checker-container">
      <div className="glass-background"></div>

      {/* ⭐ FIXED NAVBAR WITH LOGO */}
      <nav className="checker-nav fade-in">
        <div className="nav-brand" onClick={() => navigate('/')}>
          <img src="/icons/logo-512.png" alt="GrammAA Logo" className="nav-logo" />
          <span className="brand-text">GrammAA</span>
        </div>

        <div className="nav-links">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            Dashboard
          </Button>

          <Button variant="ghost" onClick={() => navigate('/history')}>
            History
          </Button>

          <div className="usage-badge">
            {user?.is_premium ? (
              <Badge className="premium-badge">Premium</Badge>
            ) : (
              <Badge variant="outline">
                {user?.remaining || 0}/{user?.daily_limit || 3} checks left
              </Badge>
            )}
          </div>
        </div>
      </nav>

      {/* ⭐ MAIN CONTENT */}
      <div className="checker-content fade-in">
        <div className="checker-main">

          {/* INPUT SECTION */}
          <div className="input-section glass-panel">
            <h2>Your Text</h2>

            <Textarea
              placeholder="Type or paste your text here..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="glass-textarea"
            />
            <Button
  onClick={toggleVoiceInput}
  className={`glass-btn voice-btn ${listening ? 'listening' : ''}`}
  size="sm"
>
  {listening ? (
    <>
      <span className="voice-icon animate-pulse">🔴</span> Listening (Click to stop)
    </>
  ) : (
    <>
      <span className="voice-icon">🎤</span> Speak Now
    </>
  )}
</Button>


            <Button
              onClick={handleCheck}
              disabled={loading || !text.trim()}
              className="glass-btn check-btn"
              size="lg"
            >
              {loading ? 'Analyzing...' : 'Fix Grammar'}
            </Button>
          </div>

          {/* RESULT SECTION */}
          {result && (
            <div className="result-section glass-panel fade-in">

              {/* HEADER */}
              <div className="result-header">
                <h2>Results</h2>

                <div className="grammar-score">
                  <span className="score-label">Grammar Score</span>
                  <span className={`score-value ${
                    result.grammar_score >= 80
                      ? 'score-high'
                      : result.grammar_score >= 60
                      ? 'score-mid'
                      : 'score-low'
                  }`}>
                    {result.grammar_score}/100
                  </span>
                </div>
              </div>

              {/* TABS */}
              <Tabs defaultValue="corrected">
                <TabsList>
                  <TabsTrigger value="corrected">Corrected</TabsTrigger>
                  <TabsTrigger value="mistakes">
                    Mistakes ({result.mistakes?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="rewrites">Rewrites</TabsTrigger>
                </TabsList>

                {/* CORRECTED TAB */}
                <TabsContent value="corrected">
                  <Card className="result-card">
                    <p className="corrected-text">{result.corrected_text}</p>
                  </Card>
                </TabsContent>

                {/* MISTAKES TAB */}
                <TabsContent value="mistakes">
                  <div className="mistakes-list">
                    {result.mistakes?.length > 0 ? (
                      result.mistakes.map((mistake, index) => (
                        <Card key={index} className="mistake-card">
                          <div className="mistake-header">
                            <Badge className={getMistakeColor(mistake.type)}>
                              {mistake.type}
                            </Badge>
                          </div>

                          <div className="mistake-content">
                            <div className="mistake-change">
                              <span className="mistake-original">{mistake.original}</span>
                              <span className="arrow">→</span>
                              <span className="mistake-corrected">{mistake.corrected}</span>
                            </div>

                            <p className="mistake-explanation">{mistake.explanation}</p>
                          </div>
                        </Card>
                      ))
                    ) : (
                      <p className="no-mistakes">No mistakes found! Great job!</p>
                    )}
                  </div>
                </TabsContent>

                {/* REWRITES TAB */}
                <TabsContent value="rewrites">
                  <div className="rewrites-list">
                    {Object.entries(result.rewrites || {}).map(([style, text]) => (
                      <Card key={style} className="rewrite-card">
                        <h3 className="rewrite-style">
                          {style.charAt(0).toUpperCase() + style.slice(1)}
                        </h3>
                        <p className="rewrite-text">{text}</p>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checker;
